import React from 'react';
import {Box, ButtonBase} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import BuildIcon from '@mui/icons-material/Build';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import {NAVY, TEXTE, TITRE_SECTION, VERDICT_COULEURS} from '../../tokens.js';
import {BadgeTypologie} from './FicheBadges.js';
import {formatDate, titreBandeau} from './fiche.selectors.js';

const GLYPHES = {ras: CheckIcon, pb: BuildIcon, imp: CloseIcon};
const HALOS = {ras: 'rgba(135,188,38,.22)', pb: 'rgba(243,154,63,.22)', imp: 'rgba(178,34,34,.22)', ni: 'rgba(181,188,198,.22)'};
const SEPARATEURS = {ras: 'rgba(92,131,22,.25)', pb: 'rgba(184,100,26,.25)', imp: 'rgba(178,34,34,.25)', ni: 'rgba(138,146,156,.25)'};

function Colonne({titre, valeur, couleur}) {
    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '2px'}}>
            <Box component="dt" sx={{fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: TITRE_SECTION}}>{titre}</Box>
            <Box component="dd" sx={{m: 0, fontSize: 16, fontWeight: 700, whiteSpace: 'nowrap', color: couleur || TEXTE}}>{valeur}</Box>
        </Box>
    );
}

/**
 * Bandeau verdict (R2) : état du dernier passage. Une seule ligne `nowrap` (défilement horizontal si l'écran est trop étroit).
 * `passage` : sortie de `dernierPassage`, `verdict` : de `verdictPassage`.
 */
export default function FicheBandeau({pav, verdict, avecLavage, passage, elementDefaut, prochain, onPdf}) {
    const c = VERDICT_COULEURS[verdict] || VERDICT_COULEURS.ras;
    const Glyphe = GLYPHES[verdict] || CheckIcon;
    const quantite = pav.quantite != null ? `${pav.quantite} abri-bac${pav.quantite > 1 ? 's' : ''}` : null;
    const heure = passage.heure ? ` · ${passage.heure}` : '';
    return (
        <Box component="section" aria-label="État du point" sx={{overflowX: 'auto', borderRadius: '16px', border: `1px solid ${c.bordure}`, background: c.bandeau || '#F6F8F4'}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: '28px', px: '28px', py: '20px', flexWrap: 'nowrap', minWidth: 'max-content'}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: '18px'}}>
                    <Box component="span" sx={{width: 52, height: 52, borderRadius: '50%', bgcolor: c.pastille, boxShadow: `0 0 0 8px ${HALOS[verdict] || HALOS.ni}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none'}}>
                        <Glyphe sx={{fontSize: 26, color: '#FFFFFF'}}/>
                    </Box>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                        <Box component="h1" sx={{m: 0, fontSize: 24, lineHeight: 1.15, fontWeight: 800, color: c.texte, whiteSpace: 'nowrap'}}>{titreBandeau(verdict, avecLavage)}</Box>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: '10px', fontSize: 14, color: TEXTE, whiteSpace: 'nowrap'}}>
                            <span><Box component="strong" sx={{fontWeight: 800, color: NAVY}}>{pav.nomenclature}</Box> · {pav.communeLibelle || '—'}</span>
                            {quantite && (<><span style={{color: TITRE_SECTION}}>·</span><span>{quantite}</span></>)}
                            <BadgeTypologie typologie={pav.typologie}/>
                        </Box>
                    </Box>
                </Box>
                <Box sx={{width: '1px', height: 44, bgcolor: SEPARATEURS[verdict] || SEPARATEURS.ni, flex: 'none'}}/>
                <Box component="dl" sx={{m: 0, display: 'flex', gap: '32px', flexWrap: 'nowrap'}}>
                    <Colonne titre="Dernier passage" valeur={`${formatDate(passage.date)}${heure}`}/>
                    <Colonne titre="Élément en défaut" valeur={elementDefaut || '—'} couleur={elementDefaut ? '#B22222' : TITRE_SECTION}/>
                    <Colonne titre="Prochain passage" valeur={formatDate(prochain)} couleur={prochain ? TEXTE : TITRE_SECTION}/>
                </Box>
                <Box sx={{flex: '1 1 0', minWidth: '16px'}}/>
                {passage.interventionId != null && (
                    <ButtonBase onClick={() => onPdf(passage.interventionId)} sx={{flex: 'none', gap: '8px', height: 40, px: '18px', borderRadius: '999px', bgcolor: NAVY, color: '#FFFFFF', fontSize: 12, fontWeight: 800, letterSpacing: '.08em', '&:hover': {bgcolor: '#0F2D69'}}}>
                        <PictureAsPdfOutlinedIcon sx={{fontSize: 16}}/>
                        FICHE PDF
                    </ButtonBase>
                )}
            </Box>
        </Box>
    );
}
